import { Stack } from '@mui/material';
import { GetRowIdParams, GridApi, GridReadyEvent } from 'ag-grid-community';
import { useAtomValue } from 'jotai';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { AgTable } from '../../../../components/AgTable/AgTable';
import { FadeInGridWrapper } from '../../../../components/grid-renderers/FadeInGridWrapper';
import { kpiTemplateAssignmentsListStateJ } from '../../../../services/state/KPI/KPICompaniesStateJ';
import { groupAwareProcessCb } from '../../../../components/AgTable/exportToExcelDefs';
import { ExportButtonContainer } from '../KPIRequests/components/KpiStyleDefs';
import { ExportGridButton } from '../../../../components/AgTable/ExportGridButton';
import { clientConfigFieldsAtom } from '../../../../services/state/AppConfigStateJ';
import { MaggieFeatureFlags } from '../../../../util/feature-flags';
import { AssignModal } from './AllCompaniesForms/AssignModal';
import { CreateRequestModal } from './AllCompaniesForms/CreateRequestModal';
import { DeleteAssignmentModal } from './AllCompaniesForms/DeleteAssignmentModal';
import { useAllKpiCompaniesActions } from './AllCompaniesForms/useAllKpiCompaniesActions';
import { allCompaniesActionStateJ } from './AllCompaniesStateJ';
import { defaultColDef, defaultColGroupDef, useAssignedCompaniesColDefs } from './AssignedCompaniesColDefs';
import { IAssignedRowData, useAssignedCompaniesData } from './AssignedCompaniesData';
import { useAssignedFilter } from './hooks/useAssignedFilter';

function AssignedCompaniesModals() {
  const actionState = useAtomValue(allCompaniesActionStateJ);
  const isConsolidated =
    (useAtomValue(clientConfigFieldsAtom)?.Using_Consolidated_Data_Collection as boolean) ?? false;
  const { useStarbreakerCollectionV2 } = useFlags<MaggieFeatureFlags>();
  const isConsolidatedV2 = isConsolidated && useStarbreakerCollectionV2;
  if (!actionState) {
    return null;
  }
  return (
    <>
      {actionState === 'createRequest' && !isConsolidatedV2 && <CreateRequestModal />}
      {actionState === 'deleteAssignment' && <DeleteAssignmentModal />}
      {actionState === 'editAssignment' && <AssignModal />}
    </>
  );
}

function AssignedCompaniesContent() {
  return (
    <>
      <AssignedCompaniesModals />
      <AssignedCompaniesGrid />
    </>
  );
}

// prevents rerendering of the grid when the modals are opened
function ResetStateWrapper({ children }: PropsWithChildren) {
  const { resetUIState } = useAllKpiCompaniesActions();

  useEffect(() => {
    return resetUIState;
  }, [resetUIState]);

  return <>{children}</>;
}

export function AssignedCompanies() {
  const templates = useAtomValue(kpiTemplateAssignmentsListStateJ);

  if (!templates) {
    return null;
  }

  return (
    <ResetStateWrapper>
      <AssignedCompaniesContent />
    </ResetStateWrapper>
  );
}

export function AssignedCompaniesGrid() {
  const colDefs = useAssignedCompaniesColDefs();
  const rowData = useAssignedCompaniesData();
  const exportParams = { processCellCallback: groupAwareProcessCb, fileName: 'assigned companies' };

  const [gridApi, setGridApi] = useState<GridApi | null>(null);

  const { setGridFilter } = useAssignedFilter();
  const onGridReady = useCallback((e: GridReadyEvent) => {
    setGridApi(e.api);
  }, []);

  useEffect(() => {
    setGridFilter(gridApi);
  }, [gridApi, setGridFilter]);

  const getRowId = useCallback(
    (params: GetRowIdParams<IAssignedRowData>) =>
      !params.data ? '' : `${params.data.id}-${params.data.company?.id}`,
    []
  );

  const handleExport = useCallback(() => {
    gridApi?.exportDataAsExcel();
  }, [gridApi]);

  return (
    <Stack height={'100%'}>
      <div css={ExportButtonContainer}>
        <ExportGridButton onClick={handleExport} />
      </div>
      <FadeInGridWrapper>
        <AgTable
          rowData={rowData}
          columnDefs={colDefs}
          defaultColDef={defaultColDef}
          rowGroupPanelShow='always'
          groupDefaultExpanded={-1}
          suppressMovableColumns={true}
          suppressRowDrag={true}
          onGridReady={onGridReady}
          getRowId={getRowId}
          defaultCsvExportParams={exportParams}
          defaultExcelExportParams={exportParams}
          autoGroupColumnDef={defaultColGroupDef}
        />
      </FadeInGridWrapper>
    </Stack>
  );
}
